import { RelatorioDemonstrativoReceitaDespesaPdfResponse } from "core/features/relatorios/typings";
import { PDFLayout } from "ui/components/PDF";
import OperacaoTable from "./components/OperacaoTable";
import SummaryItem from "ui/components/PDF/components/SummaryItem";
import colors from "core/resources/theme/colors";
import { View, Text } from "@react-pdf/renderer";
import moment from "moment";
import { formatMoney } from "infra/helpers/number";
import { flattenRelatorioCategoriasTree } from "core/features/relatorios/utils/flattenRelatorioCategoriasTree";
import { OperacaoEnum } from "core/resources/enums/conta";

interface RelatorioDemonstrativoReceitaDespesaPdfProps {
  data?: RelatorioDemonstrativoReceitaDespesaPdfResponse;
  includeDocumentWrapper?: boolean;
  noPercentage?: boolean;
  titulo?: string;
  somarSubcategorias?: boolean;
  operacao?: OperacaoEnum;
}

function PDFRelatorioDemonstrativoReceitaDespesa({
  data,
  includeDocumentWrapper = true,
  noPercentage,
  titulo,
  somarSubcategorias,
  operacao,
}: RelatorioDemonstrativoReceitaDespesaPdfProps) {
  const flattenReceitas = flattenRelatorioCategoriasTree(data?.receitas || []);
  const flattenDespesas = flattenRelatorioCategoriasTree(data?.despesas || []);

  return (
    <>
      <PDFLayout
        fontSize={7}
        includeDocument={includeDocumentWrapper}
        assinaturas={data?.assinaturas}
        headerProps={{
          styles: {
            title: {
              fontWeight: "medium",
              fontSize: 10,
            },
            tenantNome: {
              fontSize: 9,
            },
            subTitle: {
              fontSize: 8,
              marginTop: 4,
            },
          },
          logo: data?.logo!,
          tenant: data?.nomeTenant!,
          title: `Demonstrativo Receitas e Despesas ${
            titulo ? ` \n ${titulo}` : ""
          } `,
          subTitle: data?.periodo,
          cards: [
            {
              title: "Total de Receitas",
              value: formatMoney(data?.totais.receita ?? 0),
              shouldHide: operacao === OperacaoEnum.Despesa,
            },
            {
              title: "Total de Despesas",
              value: formatMoney(data?.totais.despesa ?? 0),
              shouldHide: operacao === OperacaoEnum.Receita,
            },
          ],
        }}
        footerProps={{
          tenant: data?.nomeTenant!,
          logo: data?.logoAdm!,
          endereco1: data?.endereco1!,
          endereco2: data?.endereco2!,
        }}
      >
        {data ? (
          <>
            {data.dataSaldoInicio ? (
              <SummaryItem
                title={`Saldo em ${data.dataSaldoInicio}`}
                value={data.totais.saldoInicial}
                bordered
              />
            ) : null}

            {operacao !== OperacaoEnum.Despesa ? (
              <View
                style={{
                  marginTop: 6,
                  marginBottom:
                    operacao === OperacaoEnum.Receita ? 24 : undefined,
                }}
              >
                <OperacaoTable
                  noPercentage={noPercentage}
                  items={flattenReceitas}
                  tableTitle="Receitas"
                  somarSubcategorias={somarSubcategorias}
                />
              </View>
            ) : null}

            {operacao !== OperacaoEnum.Receita ? (
              <View style={{ marginTop: 12, marginBottom: 24 }}>
                <OperacaoTable
                  noPercentage={noPercentage}
                  items={flattenDespesas}
                  tableTitle="Despesas"
                  somarSubcategorias={somarSubcategorias}
                />
              </View>
            ) : null}

            {data.transferencias?.length ? (
              <View style={{ marginTop: 10 }}>
                <SummaryItem
                  title={`Transferências`}
                  leftTitle="Valor"
                  bordered
                />

                {data.transferencias.map((transferencia) => (
                  <View style={{ flexDirection: "row" }} key={transferencia.id}>
                    <Text
                      style={{
                        fontWeight: "medium",
                        marginBottom: 6,
                        color: colors.black,
                      }}
                    >
                      {moment(transferencia.data).format("DD/MM/YYYY")} - De{" "}
                      <Text
                        style={{
                          fontWeight: "medium",
                          textDecoration: "underline",
                        }}
                      >
                        {transferencia.contaOrigem}
                      </Text>{" "}
                      para{" "}
                      <Text
                        style={{
                          fontWeight: "medium",
                          textDecoration: "underline",
                        }}
                      >
                        {transferencia.contaDestino}
                      </Text>
                    </Text>
                    <Text style={{ marginLeft: "auto" }}>
                      {formatMoney(transferencia.valor)}
                    </Text>
                  </View>
                ))}
              </View>
            ) : null}

            <SummaryItem
              title={`Resultado (Receitas - Despesas)`}
              value={data.totais.resultado}
              bordered
            />

            <SummaryItem
              title={`Saldo em ${data.dataSaldoFim}`}
              value={data.totais.saldo}
            />
          </>
        ) : null}
      </PDFLayout>
    </>
  );
}

export default PDFRelatorioDemonstrativoReceitaDespesa;
